import { Result } from '@coveo/headless';
import { environment } from '@environments/environment';
import { AnalyticsService, KeplerCoveoCommonSourceName, KeplerCoveoSource, VimeoService } from '@services';
import { createCommunitySAMLURL } from '@shared/utils';
import { cleanExcerpt, getCapabilityAcceleratorTag, getValidTimestamp } from '@shared/utils/coveo/coveo-utilities';
import { createYouTubeEmbedUrl } from '@shared/utils/youtube/youtube.utilties';
import { extractVimeoId, getYouTubeDuration } from '@shared/utils';
import { firstValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { HeadlessSearchResult } from './headless-search-result.interface';

export class HeadlesssSearchResultsAdapter {
    private vimeoService?: VimeoService;
    private videoDurations = new Map<string, number | string>();
    private vimeoThumbnails = new Map<string, string>();
    private pendingRequests = new Map<string, Promise<void>>();

    constructor(vimeoService?: VimeoService) {
        this.vimeoService = vimeoService;
    }
    /**
     * Adapt a raw Coveo Result into a HeadlessSearchResult for UI rendering.
     * Handles URL transformations for YouTube embeds and community SAML links.
     * Applies cached video duration if available.
     * @param opts - Object containing the raw Coveo Result and authentication state
     * @returns Adapted HeadlessSearchResult ready for template rendering
     */
    adapt(opts: { result: Result; isAuthed: boolean }): HeadlessSearchResult {
        const { result, isAuthed } = opts;

        let currentSubTitle = (result?.raw?.sourcedisplayname as string)?.trim();
        if (!currentSubTitle) {
            currentSubTitle = result?.raw?.source;
        }
        const subtitle = this.getSubtitle(currentSubTitle);

        let url = result.clickUri;
        if (result?.raw?.ytthumbnailurl && url) {
            url = createYouTubeEmbedUrl(result.clickUri as string);
        } else if (url?.includes(environment.api.community.baseUrl) && isAuthed) {
            url = createCommunitySAMLURL(url);
        }

        const adaptedResult: HeadlessSearchResult = {
            result: result,
            title: result.title,
            subtitle: this.getSubtitle(subtitle),
            description: cleanExcerpt(result.excerpt),
            url: url,
            thumbnail: result.raw.thumbnail as string,
            ytthumbnailurl: result.raw.ytthumbnailurl as string,
            subtitleIcon: this.getSubtitleIcon(result),
            isLoadingInFrame: subtitle === KeplerCoveoCommonSourceName.DOCUMENTATION,
            timestamp: getValidTimestamp(result.raw.date),
            isDeprecated: false,
            dataAnalytics: ['search', AnalyticsService.format(result.title)],
            customTag: getCapabilityAcceleratorTag(result?.raw?.seo_facet_feature as string[], result?.title),
            sourceDisplayName: result?.raw?.sourcedisplayname as string,
            duration: this.videoDurations.get(url) as string,
        };

        return adaptedResult;
    }

    /**
     * Return subtitle source name if commonsourcename is not mapped or present
     * @param source string
     * @returns string
     */
    private getSubtitle(source: string): string {
        if (source) {
            switch (source) {
                case KeplerCoveoSource.GENESYS_ONLINE_COMMUNITY_HL:
                    return KeplerCoveoCommonSourceName.COMMUNITY;
                case KeplerCoveoSource.COMMUNITY_YOUTUBE:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_1:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_2:
                    return KeplerCoveoCommonSourceName.VIDEO;
                case KeplerCoveoSource.GENESYS_BLOG:
                // fall-through
                case KeplerCoveoSource.GENESYS_BLOG_2:
                    return KeplerCoveoCommonSourceName.BLOGS;
                case KeplerCoveoSource.DEV_CENTER:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_2:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_3:
                    return KeplerCoveoCommonSourceName.DEV_CENTER;
                case KeplerCoveoSource.BEYOND_CATALOG:
                    return KeplerCoveoCommonSourceName.EDUCATION;
                default:
                    return source;
            }
        }

        // default
        return KeplerCoveoCommonSourceName.DOCUMENTATION;
    }

    /**
     * @deprecated
     * Return Thumbnail based on commom source name
     * @param result - Coveo raw result
     * @returns thumbnail placeholder string
     */
    private getThumbnail(result: Result): string {
        if (result.raw?.commonsourcename) {
            switch (result.raw.commonsourcename) {
                case KeplerCoveoCommonSourceName.KNOWLEDGE_BASE:
                    return '/assets/icons/support/recommendations/knowledge-base.svg';
                case KeplerCoveoCommonSourceName.TRAINING:
                    return '/assets/icons/support/recommendations/training.svg';
                case KeplerCoveoCommonSourceName.VIDEO:
                    return '/assets/icons/support/recommendations/video.svg';
                case KeplerCoveoCommonSourceName.DOCUMENTATION:
                // fall-through
                default:
                    return '/assets/icons/support/recommendations/documentation.svg';
            }
        } else if (result.raw?.source) {
            switch (result.raw.source) {
                case KeplerCoveoSource.GENESYS_ONLINE_COMMUNITY_HL:
                    return '/assets/icons/support/recommendations/community.svg';
                case KeplerCoveoSource.COMMUNITY_YOUTUBE:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_1:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_2:
                    return '/assets/icons/support/recommendations/video.svg';
                case KeplerCoveoSource.GENESYS_BLOG:
                // fall-through
                case KeplerCoveoSource.GENESYS_BLOG_2:
                    return '/assets/icons/support/recommendations/blog.svg';
                case KeplerCoveoSource.DEV_CENTER:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_2:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_3:
                    return '/assets/icons/support/recommendations/dev-center.svg';
            }
        }

        // default
        return '/assets/icons/support/recommendations/documentation.svg';
    }

    /**
     * @deprecated
     * Return Thumbnail based on commom source name
     * @param result - Coveo raw result
     * @returns thumbnail placeholder string
     */
    private getSubtitleIcon(result: Result): string {
        if (result.raw?.commonsourcename) {
            switch (result.raw.commonsourcename) {
                case KeplerCoveoCommonSourceName.KNOWLEDGE_BASE:
                    return '/assets/icons/support/recommendations/knowledge-base-white.svg';
                case KeplerCoveoCommonSourceName.TRAINING:
                    return '/assets/icons/support/recommendations/training-white.svg';
                case KeplerCoveoCommonSourceName.VIDEO:
                    return '/assets/icons/support/recommendations/video-white.svg';
            }
        } else if (result.raw?.source) {
            switch (result.raw.source) {
                case KeplerCoveoSource.GENESYS_ONLINE_COMMUNITY_HL:
                    return '/assets/icons/support/recommendations/community-white.svg';
                case KeplerCoveoSource.COMMUNITY_YOUTUBE:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_1:
                // fall-through
                case KeplerCoveoSource.COMMUNITY_YOUTUBE_2:
                    return '/assets/icons/support/recommendations/video-white.svg';
                case KeplerCoveoSource.GENESYS_BLOG:
                // fall-through
                case KeplerCoveoSource.GENESYS_BLOG_2:
                    return '/assets/icons/support/recommendations/blog-white.svg';
                case KeplerCoveoSource.DEV_CENTER:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_2:
                // fall-through
                case KeplerCoveoSource.DEV_CENTER_3:
                    return '/assets/icons/support/recommendations/dev-center-white.svg';
            }
        }

        // default
        return '/assets/icons/support/recommendations/documentation-white.svg';
    }

    /**
     * Fetch durations and thumbnails for all Vimeo and YouTube video results in parallel.
     * Uses a shared-promise pattern to deduplicate concurrent requests for the same URL.
     * Applies cached data to result objects on subsequent calls.
     * @param results - Array of HeadlessSearchResult to enrich with video metadata
     */
    async fetchVideoDurations(results: HeadlessSearchResult[]): Promise<void> {
        if (!this.vimeoService) {
            console.warn('VimeoService not provided to adapter');
            return;
        }

        const promises = results.map(async (result) => {
            // Apply cached data to new result objects
            if (this.videoDurations.has(result.url)) {
                result.duration = this.videoDurations.get(result.url) as string;
                const cachedThumbnail = this.vimeoThumbnails.get(result.url);
                if (cachedThumbnail && !result.thumbnail) {
                    result.thumbnail = cachedThumbnail;
                }
                return;
            }

            // Await existing in-flight request for same URL, then apply cached data
            if (this.pendingRequests.has(result.url)) {
                await this.pendingRequests.get(result.url);
                result.duration = this.videoDurations.get(result.url) as string;
                const cachedThumbnail = this.vimeoThumbnails.get(result.url);
                if (cachedThumbnail && !result.thumbnail) {
                    result.thumbnail = cachedThumbnail;
                }
                return;
            }

            const vimeoId = extractVimeoId(result.url);
            if (vimeoId) {
                const request = this.fetchVimeoDetails(result, vimeoId);
                this.pendingRequests.set(result.url, request);
                await request;
                this.pendingRequests.delete(result.url);
            } else if (result.ytthumbnailurl) {
                const request = this.fetchYouTubeDuration(result);
                this.pendingRequests.set(result.url, request);
                await request;
                this.pendingRequests.delete(result.url);
            }
        });
        await Promise.all(promises);
    }

    /**
     * Get cached Vimeo thumbnail
     * @param url - Result URL
     * @returns Vimeo thumbnail URL or undefined
     */
    getVimeoThumbnail(url: string): string | undefined {
        return this.vimeoThumbnails.get(url);
    }

    /**
     * Fetch Vimeo video details (duration and thumbnail) and cache them.
     * @param result - HeadlessSearchResult to enrich with Vimeo metadata
     * @param vimeoId - Extracted Vimeo video ID
     */
    private async fetchVimeoDetails(result: HeadlessSearchResult, vimeoId: string): Promise<void> {
        try {
            const vimeoData = await firstValueFrom(this.vimeoService.getVimeoVideoDetails(Number.parseInt(vimeoId)));
            if (vimeoData?.duration) {
                const duration = dayjs(vimeoData.duration * 1000).toISOString();
                this.videoDurations.set(result.url, duration);
                result.duration = duration;
            }
            if (vimeoData?.thumbnail_url) {
                this.vimeoThumbnails.set(result.url, vimeoData.thumbnail_url);
                if (!result.thumbnail) {
                    result.thumbnail = vimeoData.thumbnail_url;
                }
            }
        } catch (error) {
            console.error('Failed to get Vimeo video details:', error);
        }
    }

    /**
     * Fetch YouTube video duration and cache it.
     * @param result - HeadlessSearchResult to enrich with YouTube duration
     */
    private async fetchYouTubeDuration(result: HeadlessSearchResult): Promise<void> {
        try {
            const duration = await getYouTubeDuration(result.url, environment.youtube.key);
            if (duration) {
                this.videoDurations.set(result.url, duration);
                result.duration = duration;
            }
        } catch (error) {
            console.error('Failed to get YouTube duration:', error);
        }
    }
}
